import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../utils/api';
import { Search, BookOpen, X, Eye, Check } from 'lucide-react';

/**
 * Reusable KB article search + select modal.
 * Props:
 *   onSelect(article) — called when an article is picked
 *   onClose() — close the modal
 *   excludeIds — array of article IDs to exclude (already linked)
 */
export default function KBArticlePicker({ onSelect, onClose, excludeIds = [] }) {
  const { t, i18n } = useTranslation();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [recentArticles, setRecentArticles] = useState([]);

  // Load recent articles on mount
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/kb/articles?status=published&limit=10');
        setRecentArticles(res.data.articles || []);
      } catch {}
    })();
  }, []);

  // Debounced search
  useEffect(() => {
    if (query.length < 2) { setResults([]); return; }
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const res = await api.get(`/kb/search?q=${encodeURIComponent(query)}`);
        setResults(res.data.articles || []);
      } catch { setResults([]); }
      setLoading(false);
    }, 300);
    return () => clearTimeout(timer);
  }, [query]);

  const displayArticles = query.length >= 2 ? results : recentArticles;
  const filtered = displayArticles.filter(a => !excludeIds.includes(a.id));

  return (
    <div className="modal-backdrop" onMouseDown={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 550, maxHeight: '80vh', display: 'flex', flexDirection: 'column' }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <BookOpen size={18} /> {t('kb.linkArticle')}
          </h2>
          <button className="btn btn-ghost" onClick={onClose}><X size={18} /></button>
        </div>

        {/* Search */}
        <div style={{ padding: '12px 20px 0' }}>
          <div style={{ position: 'relative' }}>
            <Search size={16} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--c-text-muted)' }} />
            <input
              className="form-input"
              style={{ paddingLeft: 34, width: '100%' }}
              placeholder={t('kb.searchPlaceholder')}
              value={query}
              onChange={e => setQuery(e.target.value)}
              autoFocus
            />
          </div>
        </div>

        {/* Results */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '12px 20px 20px' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 20 }}><div className="spinner" /></div>
          ) : filtered.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 20, color: 'var(--c-text-muted)', fontSize: 13 }}>
              {query.length >= 2 ? t('kb.noResults') : t('kb.typeToSearch')}
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {!query && <div style={{ fontSize: 11, color: 'var(--c-text-muted)', marginBottom: 4 }}>{t('kb.recentArticles')}</div>}
              {filtered.map(article => (
                <div
                  key={article.id}
                  className="kb-picker-item"
                  onClick={() => onSelect(article)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px',
                    borderRadius: 8, cursor: 'pointer', border: '1px solid var(--c-border)',
                    transition: 'background 0.15s',
                  }}
                >
                  <BookOpen size={14} style={{ color: 'var(--c-text-muted)', flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{article.title}</div>
                    {article.category_name && (
                      <span style={{ fontSize: 11, color: 'var(--c-text-muted)' }}>{article.category_icon} {i18n.language === 'en' ? (article.category_name_en || article.category_name) : (article.category_name_es || article.category_name)}</span>
                    )}
                  </div>
                  <span style={{ fontSize: 11, color: 'var(--c-text-muted)', display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Eye size={11} /> {article.view_count || 0}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
